import * as React from "react"
import { graphql, Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import styled from "styled-components"

import {
  buildCampEventLocation,
  buildCampEventOffers,
  buildCampEventOrganizers,
  getCampEventEndDate,
  getCampEventStartDate,
} from "../../config/camp-event-schema"
import Layout from "../components/layout"
import SEO from "../components/seo"
import {
  buildPersonSchema,
  buildSiteEntityIds,
  toAbsoluteUrl,
} from "../components/schema-utils"

const PAGE_PATH = "/kampfsport-camps-deutschland/"
const ABOUT_PAGE_PATH = "/ueber-uns/"
const TLA_HOSTED_EVENT_IDS = new Set([
  "rob-biernacki-cal-jones-cla-intensive-camp-2026",
  "shieldmaiden-summit-ii-cla-intensive-2026",
  "ed-ingamells-contemporary-skill-acquisition-grappling-2026",
])
const COACH_ENTITY_CONFIG = {
  "Laurien Zurhake": {
    fragment: "laurien-zurhake",
    description:
      "Laurien ist Headcoach bei Team Laurien & Alex in München, BJJ Brown Belt, aktive Wettkämpferin, Yoga-Lehrerin und trauma-informierte Coachin.",
    jobTitle: "Head Coach, BJJ Brown Belt, Yoga-Lehrerin",
    sameAs: ["https://www.instagram.com/laurienzurhake/"],
    knowsAbout: [
      "Brazilian Jiu-Jitsu",
      "NoGi",
      "Yoga",
      "Kindertraining",
      "Trauma-informierter Unterricht",
    ],
  },
  "Alex Zurhake": {
    fragment: "alex-zurhake",
    description:
      "Alex ist Headcoach bei Team Laurien & Alex in München, BJJ Brown Belt, 2. Dan Ju-Jutsu, aktiver MMA-, K1- und BJJ-Kämpfer sowie Yoga-Lehrer.",
    jobTitle: "Head Coach, BJJ Brown Belt, 2. Dan Ju-Jutsu",
    sameAs: [
      "https://www.linkedin.com/in/alexander-zurhake-7351b4187/",
      "https://www.instagram.com/alexzurhake/",
    ],
    knowsAbout: [
      "Brazilian Jiu-Jitsu",
      "Mixed Martial Arts",
      "Kickboxen",
      "Yoga",
      "Trauma-informierter Unterricht",
    ],
  },
}

const BRAND = {
  dark: "#181B2F",
  orange: "#FF6E00",
  orangeSoft: "#FFF1E5",
  neutral: "#5E6A73",
}
const SITE_TIME_ZONE = "Europe/Berlin"

const siteReferenceDateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: SITE_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

const fullDateFormatter = new Intl.DateTimeFormat("de-DE", {
  timeZone: "UTC",
  day: "numeric",
  month: "long",
  year: "numeric",
})

const monthYearFormatter = new Intl.DateTimeFormat("de-DE", {
  timeZone: "UTC",
  month: "long",
  year: "numeric",
})

const monthFormatter = new Intl.DateTimeFormat("de-DE", {
  timeZone: "UTC",
  month: "long",
})

function parseIsoDate(isoDate) {
  const [year, month, day] = isoDate.split("-").map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

function getCurrentReferenceDate(now = new Date()) {
  const parts = siteReferenceDateFormatter.formatToParts(now)
  const year = parts.find(({ type }) => type === "year")?.value
  const month = parts.find(({ type }) => type === "month")?.value
  const day = parts.find(({ type }) => type === "day")?.value

  if (!year || !month || !day) {
    return now.toISOString().slice(0, 10)
  }

  return `${year}-${month}-${day}`
}

function formatGermanDateRange(startDate, endDate) {
  if (startDate === endDate) {
    return fullDateFormatter.format(parseIsoDate(startDate))
  }

  const start = parseIsoDate(startDate)
  const end = parseIsoDate(endDate)
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear()
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth()

  if (sameMonth) {
    return `${start.getUTCDate()}.–${end.getUTCDate()}. ${monthYearFormatter.format(
      end,
    )}`
  }

  if (sameYear) {
    return `${start.getUTCDate()}. ${monthFormatter.format(
      start,
    )} – ${end.getUTCDate()}. ${monthYearFormatter.format(end)}`
  }

  return `${fullDateFormatter.format(start)} – ${fullDateFormatter.format(end)}`
}

function buildCampGroups(events, referenceDate) {
  const upcoming = []
  const past = []

  events.forEach((event) => {
    if (event.endDate >= referenceDate) {
      upcoming.push(event)
      return
    }

    past.push(event)
  })

  upcoming.sort((a, b) => a.startDate.localeCompare(b.startDate))
  past.sort((a, b) => b.startDate.localeCompare(a.startDate))

  return { upcoming, past }
}

function useClientReferenceDate(initialReferenceDate) {
  const [referenceDate, setReferenceDate] = React.useState(
    initialReferenceDate || getCurrentReferenceDate(),
  )

  React.useEffect(() => {
    setReferenceDate(getCurrentReferenceDate())
  }, [])

  return referenceDate
}

function isExternalLink(href) {
  return /^(https?:|mailto:)/i.test(href)
}

function buildCampImageMap(fileNodes) {
  const imageMap = new Map()

  fileNodes.forEach((node) => {
    if (!node?.relativePath || !node?.childImageSharp?.gatsbyImageData) {
      return
    }

    const normalized = node.relativePath.replace(/\\/g, `/`)
    const fileName = normalized.split(`/`).pop()

    imageMap.set(normalized, node.childImageSharp.gatsbyImageData)

    if (fileName) {
      imageMap.set(fileName, node.childImageSharp.gatsbyImageData)
      imageMap.set(`images/${fileName}`, node.childImageSharp.gatsbyImageData)
    }
  })

  return imageMap
}

function getCampImageData(imageMap, picture) {
  const cleaned = String(picture || "")
    .trim()
    .replace(/\\/g, `/`)
    .replace(/^\.\//, ``)
  const fileName = cleaned.split(`/`).pop()

  return (
    imageMap.get(cleaned) ||
    imageMap.get(fileName) ||
    imageMap.get(`images/${fileName}`) ||
    null
  )
}

function buildCampImageUrlMap(fileNodes, siteUrl) {
  const imageMap = new Map()

  fileNodes.forEach((node) => {
    if (!node?.relativePath || !node?.publicURL) {
      return
    }

    const normalized = node.relativePath.replace(/\\/g, `/`)
    const fileName = normalized.split(`/`).pop()
    const absoluteUrl = toAbsoluteUrl(siteUrl, node.publicURL)

    imageMap.set(normalized, absoluteUrl)

    if (fileName) {
      imageMap.set(fileName, absoluteUrl)
      imageMap.set(`images/${fileName}`, absoluteUrl)
    }
  })

  return imageMap
}

function getCampImageUrl(imageMap, picture) {
  const cleaned = String(picture || "")
    .trim()
    .replace(/\\/g, `/`)
    .replace(/^\.\//, ``)
  const fileName = cleaned.split(`/`).pop()

  return (
    imageMap.get(cleaned) ||
    imageMap.get(fileName) ||
    imageMap.get(`images/${fileName}`) ||
    null
  )
}

function buildCoachSchemaInfo(siteUrl) {
  const aboutPageUrl = toAbsoluteUrl(siteUrl, ABOUT_PAGE_PATH)

  return Object.entries(COACH_ENTITY_CONFIG).map(([name, config]) => {
    const id = `${aboutPageUrl}#${config.fragment}`

    return {
      name,
      id,
      node: buildPersonSchema({
        siteUrl,
        id,
        url: aboutPageUrl,
        name,
        description: config.description,
        jobTitle: config.jobTitle,
        sameAs: config.sameAs,
        knowsAbout: config.knowsAbout,
      }),
    }
  })
}

function buildCampItemListSchema({
  events,
  pageUrl,
  referenceDate,
  imageUrlMap,
  siteUrl,
  coachIdByName,
}) {
  const itemListId = `${pageUrl}#event-list`
  const referenceDateValue = referenceDate || getCurrentReferenceDate()
  const siteEntityIds = buildSiteEntityIds(siteUrl)

  return {
    "@type": "ItemList",
    "@id": itemListId,
    name: "Kampfsport Camps & Seminare Übersicht",
    numberOfItems: events.length,
    itemListElement: events.map((event, index) => {
      const eventId = `${pageUrl}#event-${event.id}`
      const imageUrl = getCampImageUrl(imageUrlMap, event.picture)
      const isTlaHosted = TLA_HOSTED_EVENT_IDS.has(event.id)
      const eventHasEnded = event.endDate < referenceDateValue

      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Event",
          "@id": eventId,
          name: event.name,
          description: event.description,
          startDate: getCampEventStartDate(event),
          endDate: getCampEventEndDate(event),
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          location: buildCampEventLocation(event, {
            isTlaHosted,
            businessLocationId: siteEntityIds.businessLocation,
          }),
          organizer: buildCampEventOrganizers(event, {
            isTlaHosted,
            organizationId: siteEntityIds.organization,
          }),
          offers: buildCampEventOffers(event, { eventHasEnded }),
          maximumAttendeeCapacity: event.maximumAttendeeCapacity || undefined,
          performer: event.coaches.map((coach) => ({
            ...(coachIdByName.has(coach)
              ? { "@id": coachIdByName.get(coach) }
              : { "@type": "Person", name: coach }),
          })),
          image: imageUrl ? [imageUrl] : undefined,
          url: event.externalLink,
        },
      }
    }),
  }
}

function buildCampCollectionSchemaNodes({
  title,
  description,
  pageUrl,
  referenceDate,
  events,
  fileNodes,
  defaultSchemaNodes = [],
  siteUrl,
}) {
  const supportingNodes = defaultSchemaNodes.filter(
    (node) => node?.["@type"] !== "WebPage",
  )
  const basePageNode = defaultSchemaNodes.find(
    (node) => node?.["@type"] === "WebPage",
  )
  const imageUrlMap = buildCampImageUrlMap(fileNodes, siteUrl)
  const coachSchemaInfo = buildCoachSchemaInfo(siteUrl)
  const referencedCoachNames = new Set(
    events.flatMap((event) => event.coaches || []),
  )
  const referencedCoachNodes = coachSchemaInfo.filter(({ name }) =>
    referencedCoachNames.has(name),
  )
  const coachIdByName = new Map(
    referencedCoachNodes.map(({ name, id }) => [name, id]),
  )
  const itemListSchema = buildCampItemListSchema({
    events,
    pageUrl,
    referenceDate,
    imageUrlMap,
    siteUrl,
    coachIdByName,
  })

  const collectionPageSchema = {
    ...(basePageNode || {}),
    "@type": "CollectionPage",
    "@id": `${pageUrl}#collection-page`,
    url: pageUrl,
    name: title,
    description,
    mainEntity: { "@id": itemListSchema["@id"] },
  }

  return [
    ...supportingNodes,
    collectionPageSchema,
    ...referencedCoachNodes.map(({ node }) => node),
    itemListSchema,
  ]
}

const PageWrap = styled.div`
  background:
    radial-gradient(
      circle at top left,
      rgba(255, 110, 0, 0.08),
      transparent 32%
    ),
    linear-gradient(180deg, #fff8f3 0%, #ffffff 18%, #fffaf6 100%);
`

const Section = styled.section`
  padding: clamp(40px, 7vw, 88px) 0;
`

const Container = styled.div`
  width: min(1240px, calc(100% - 32px));
  margin: 0 auto;
`

const IntroWrap = styled.div`
  max-width: 760px;
  display: grid;
  gap: 18px;
  text-align: left;
`

const IntroHeading = styled.h1`
  margin: 0;
  color: ${BRAND.dark};
  font-size: clamp(2rem, 4vw, 3.4rem);
  line-height: 1.04;
  text-wrap: balance;
`

const IntroText = styled.p`
  margin: 0;
  color: ${BRAND.neutral};
  font-size: clamp(1.02rem, 1.5vw, 1.18rem);
  line-height: 1.72;
  max-width: 64ch;
`

const InlineLink = styled(Link)`
  color: ${BRAND.dark};
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: rgba(255, 110, 0, 0.4);
  text-underline-offset: 0.14em;
  transition:
    color 160ms ease,
    text-decoration-color 160ms ease;

  &:hover,
  &:focus-visible {
    color: ${BRAND.orange};
    text-decoration-color: currentColor;
  }
`

const ContentStack = styled.div`
  display: grid;
  gap: clamp(34px, 5vw, 54px);
`

const SectionBlock = styled.section`
  display: grid;
  gap: 18px;
`

const SectionHeading = styled.h2`
  margin: 0;
  color: ${BRAND.dark};
  font-size: clamp(1.55rem, 2.5vw, 2rem);
  line-height: 1.12;
  text-align: left;
`

const Grid = styled.div`
  display: grid;
  gap: 20px;

  @media (min-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1120px) {
    grid-template-columns: repeat(
      ${({ $columns = 2 }) => $columns},
      minmax(0, 1fr)
    );
  }
`

const Card = styled.article`
  display: grid;
  background: ${({ $tone }) => ($tone === "past" ? "#fffdfa" : "#ffffff")};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "past" ? "rgba(24, 27, 47, 0.08)" : "rgba(24, 27, 47, 0.1)"};
  border-radius: 28px;
  overflow: hidden;
  box-shadow: ${({ $tone }) =>
    $tone === "past"
      ? "0 18px 36px rgba(24, 27, 47, 0.08)"
      : "0 24px 48px rgba(24, 27, 47, 0.11)"};
`

const CardMedia = styled.div`
  position: relative;
  aspect-ratio: 1.45;
  background:
    radial-gradient(
      circle at top right,
      rgba(255, 110, 0, 0.2),
      transparent 36%
    ),
    linear-gradient(145deg, ${BRAND.dark} 0%, #2c3553 100%);
`

const CardImage = styled(GatsbyImage)`
  width: 100%;
  height: 100%;

  img {
    object-fit: cover;
  }
`

const CardImageFallback = styled.div`
  width: 100%;
  height: 100%;
`

const CardBody = styled.div`
  display: grid;
  gap: 14px;
  padding: 22px;
  text-align: left;
`

const CardTitle = styled.h3`
  margin: 0;
  color: ${BRAND.dark};
  font-size: clamp(1.25rem, 1.7vw, 1.55rem);
  line-height: 1.15;
  text-wrap: balance;
`

const CardDescription = styled.p`
  margin: 0;
  color: ${BRAND.neutral};
  font-size: 0.98rem;
  line-height: 1.68;
`

const MetaList = styled.dl`
  display: grid;
  gap: 12px;
  margin: 0;
`

const MetaRow = styled.div`
  display: grid;
  gap: 4px;
`

const MetaIconRow = styled.div`
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 10px;
  align-items: start;
`

const MetaLabel = styled.dt`
  margin: 0;
  color: ${({ $accent }) => ($accent ? BRAND.orange : BRAND.neutral)};
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const MetaLabelHidden = styled.dt`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const MetaValue = styled.dd`
  margin: 0;
  color: ${BRAND.dark};
  font-size: 1rem;
  line-height: 1.62;
`

const MetaIcon = styled.span`
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  margin-top: 4px;
  color: ${BRAND.orange};

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
`

const EventLink = styled.a`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  background: ${BRAND.orange};
  color: #fff;
  font-weight: 800;
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    background-color 180ms ease;
  box-shadow: 0 16px 28px rgba(255, 110, 0, 0.22);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 34px rgba(255, 110, 0, 0.28);
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 110, 0, 0.24);
    outline-offset: 3px;
  }
`

const EmptyState = styled.div`
  padding: 22px 24px;
  border-radius: 22px;
  border: 1px solid rgba(24, 27, 47, 0.08);
  background: rgba(255, 255, 255, 0.82);
  color: ${BRAND.neutral};
  text-align: left;
`

const LocationBlock = styled.section`
  display: grid;
  gap: 20px;
  padding: clamp(24px, 4vw, 34px);
  border-radius: 32px;
  border: 1px solid rgba(255, 110, 0, 0.1);
  background:
    radial-gradient(
      circle at top right,
      rgba(255, 110, 0, 0.12),
      transparent 32%
    ),
    linear-gradient(180deg, #fffdfb 0%, #fff7f1 100%);
  box-shadow: 0 22px 44px rgba(24, 27, 47, 0.08);
  text-align: left;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    align-items: start;
  }
`

const LocationCopy = styled.div`
  display: grid;
  gap: 12px;
`

const LocationTitle = styled.h2`
  margin: 0;
  color: ${BRAND.dark};
  font-size: clamp(1.45rem, 2.3vw, 1.8rem);
  line-height: 1.16;
`

const LocationText = styled.p`
  margin: 0;
  color: ${BRAND.neutral};
  font-size: 1rem;
  line-height: 1.72;
`

const LocationList = styled.ul`
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
`

const LocationItem = styled.li`
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 12px;
  align-items: start;
  color: ${BRAND.dark};
  font-size: 1rem;
  line-height: 1.65;
`

const LocationCheck = styled.span`
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  margin-top: 4px;
  border-radius: 999px;
  background: ${BRAND.orangeSoft};
  color: ${BRAND.orange};

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: currentColor;
  }
`

const LocationAction = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;

  @media (min-width: 900px) {
    justify-content: flex-start;
  }
`

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x="3.5"
        y="5.5"
        width="17"
        height="15"
        rx="3"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M7.5 3.5V7.5M16.5 3.5V7.5M3.5 10.5H20.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 20.5C12 20.5 18 14.6 18 10.3C18 6.9 15.3 4.5 12 4.5C8.7 4.5 6 6.9 6 10.3C6 14.6 12 20.5 12 20.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.2" fill="currentColor" />
    </svg>
  )
}

function EventCard({ event, image, tone = "upcoming" }) {
  const external = isExternalLink(event.externalLink)

  return (
    <Card $tone={tone}>
      <CardMedia>
        {image ? (
          <CardImage
            image={image}
            alt={`${event.name} bei Team Laurien & Alex`}
          />
        ) : (
          <CardImageFallback aria-hidden="true" />
        )}
      </CardMedia>

      <CardBody>
        <CardTitle>{event.name}</CardTitle>
        <CardDescription>{event.description}</CardDescription>

        <MetaList>
          <MetaRow>
            <MetaLabel $accent>Coaches</MetaLabel>
            <MetaValue>{event.coaches.join(", ")}</MetaValue>
          </MetaRow>

          <MetaRow>
            <MetaLabelHidden>Datum</MetaLabelHidden>
            <MetaIconRow>
              <MetaIcon>
                <CalendarIcon />
              </MetaIcon>
              <MetaValue>
                {formatGermanDateRange(event.startDate, event.endDate)}
              </MetaValue>
            </MetaIconRow>
          </MetaRow>

          <MetaRow>
            <MetaLabelHidden>Ort</MetaLabelHidden>
            <MetaIconRow>
              <MetaIcon>
                <PinIcon />
              </MetaIcon>
              <MetaValue>{event.location}</MetaValue>
            </MetaIconRow>
          </MetaRow>
        </MetaList>

        <EventLink
          href={event.externalLink}
          target={
            external && !event.externalLink.startsWith("mailto:")
              ? "_blank"
              : undefined
          }
          rel={
            external && !event.externalLink.startsWith("mailto:")
              ? "noopener noreferrer"
              : undefined
          }
        >
          Zum Event
        </EventLink>
      </CardBody>
    </Card>
  )
}

const CampsOverviewPage = ({ data, location }) => {
  const initialReferenceDate =
    data?.siteBuildMetadata?.buildTime || getCurrentReferenceDate()
  const campEvents = data?.allCampEvent?.nodes || []
  const campImageMap = React.useMemo(
    () => buildCampImageMap(data?.allFile?.nodes || []),
    [data?.allFile?.nodes],
  )
  const referenceDate = useClientReferenceDate(initialReferenceDate)
  const { upcoming, past } = React.useMemo(
    () => buildCampGroups(campEvents, referenceDate),
    [campEvents, referenceDate],
  )

  return (
    <Layout pathname={location?.pathname}>
      <PageWrap>
        <Section>
          <Container>
            <ContentStack>
              <IntroWrap>
                <IntroHeading>
                  Kampfsport Camps von & mit Team Laurien & Alex
                </IntroHeading>
                <IntroText>
                  Hier findest du kommende, laufende und vergangene Camps und
                  Seminare mit{" "}
                  <InlineLink to="/ueber-uns/">
                    Coaches von Team Laurien & Alex
                  </InlineLink>{" "}
                  sowie Seminare, die wir bei uns mit Gastcoaches organisieren.
                  Selbstverständlich kannst du auch an unserem regulären{" "}
                  <InlineLink to="/bjj-muenchen/">
                    BJJ-Training in München
                  </InlineLink>{" "}
                  teilnehmen.
                </IntroText>
              </IntroWrap>

              <SectionBlock>
                <SectionHeading>Bevorstehende Camps & Seminare</SectionHeading>
                {upcoming.length ? (
                  <Grid $columns={2}>
                    {upcoming.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        image={getCampImageData(campImageMap, event.picture)}
                        tone="upcoming"
                      />
                    ))}
                  </Grid>
                ) : (
                  <EmptyState>
                    Aktuell sind keine kommenden Camps oder Seminare
                    eingetragen.
                  </EmptyState>
                )}
              </SectionBlock>

              <SectionBlock>
                <SectionHeading>Vergangene Camps & Seminare</SectionHeading>
                {past.length ? (
                  <Grid $columns={2}>
                    {past.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        image={getCampImageData(campImageMap, event.picture)}
                        tone="past"
                      />
                    ))}
                  </Grid>
                ) : (
                  <EmptyState>
                    Bisher sind noch keine vergangenen Camps oder Seminare
                    eingetragen.
                  </EmptyState>
                )}
              </SectionBlock>

              <LocationBlock>
                <LocationCopy>
                  <LocationTitle>
                    Trainingsort in München-Feldmoching
                  </LocationTitle>
                  <LocationText>
                    Alle Camps und Seminare, die von Team Laurien & Alex
                    organisiert oder gehostet werden, finden in unserem
                    Trainingszentrum in München-Feldmoching statt.
                  </LocationText>
                  <LocationAction>
                    <EventLink href="/#anfahrt">So findest du zu uns</EventLink>
                  </LocationAction>
                </LocationCopy>

                <div>
                  <LocationList>
                    <LocationItem>
                      <LocationCheck aria-hidden="true" />
                      <span>
                        Lerchenstraße 14, Haus 3, 80995 München-Feldmoching
                      </span>
                    </LocationItem>
                    <LocationItem>
                      <LocationCheck aria-hidden="true" />
                      <span>
                        2 Große Trainingsflächen, große Umkleide und Dusche.
                      </span>
                    </LocationItem>
                    <LocationItem>
                      <LocationCheck aria-hidden="true" />
                      <span>
                        Gut erreichbar vom Flughafen München oder der
                        Innenstadt.
                      </span>
                    </LocationItem>
                    <LocationItem>
                      <LocationCheck aria-hidden="true" />
                      <span>
                        Kostenlose Parkplätze direkt vor dem Haus und in der
                        Umgebung
                      </span>
                    </LocationItem>
                  </LocationList>
                </div>
              </LocationBlock>
            </ContentStack>
          </Container>
        </Section>
      </PageWrap>
    </Layout>
  )
}

export default CampsOverviewPage

export const Head = ({ data, location }) => {
  const campEvents = data?.allCampEvent?.nodes || []
  const referenceDate =
    data?.siteBuildMetadata?.buildTime || getCurrentReferenceDate()
  const { upcoming, past } = buildCampGroups(campEvents, referenceDate)
  const orderedEvents = [...upcoming, ...past]

  return (
    <SEO
      title="Kampfsport Camps in Deutschland | Seminare & Events | Team Laurien & Alex"
      description="Entdecke kommende, laufende und vergangene Camps und Seminare von Team Laurien & Alex mit allen Infos zu Terminen, Coaches und Anmeldung."
      pathname={location?.pathname || PAGE_PATH}
      schemaBuilder={({
        canonicalUrl,
        defaultSchemaNodes,
        description,
        siteUrl,
        title,
      }) =>
        buildCampCollectionSchemaNodes({
          title,
          description,
          pageUrl:
            canonicalUrl ||
            toAbsoluteUrl(siteUrl, location?.pathname || PAGE_PATH),
          referenceDate,
          events: orderedEvents,
          fileNodes: data?.allFile?.nodes || [],
          defaultSchemaNodes,
          siteUrl,
        })
      }
    />
  )
}

export const query = graphql`
  query CampsOverviewPageQuery {
    siteBuildMetadata {
      buildTime(formatString: "YYYY-MM-DD")
    }
    allCampEvent(sort: { startDate: ASC }) {
      nodes {
        id
        type
        name
        description
        startDate
        endDate
        startDateTime
        endDateTime
        coaches
        location
        venueName
        streetAddress
        city
        postalCode
        region
        addressCountry
        organizers {
          name
          url
        }
        offers {
          name
          price
          priceCurrency
          availability
          url
          validFrom
        }
        maximumAttendeeCapacity
        externalLink
        picture
      }
    }
    allFile(
      filter: {
        sourceInstanceName: { eq: "camp-images" }
        extension: { in: ["jpg", "jpeg", "png", "webp", "avif"] }
      }
    ) {
      nodes {
        relativePath
        publicURL
        childImageSharp {
          gatsbyImageData(
            width: 760
            aspectRatio: 1.45
            quality: 72
            placeholder: NONE
            formats: [AUTO, WEBP]
          )
        }
      }
    }
  }
`
